import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { useSelector } from 'react-redux';
import { isNil, get, isEmpty, indexOf } from 'lodash';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { toast } from 'react-toastify';
import ToggleTabs from 'components/ToggleTabs';
import {
  makeSelectCurrentTabIndexTransaction,
  makeSelectListings,
} from 'containers/AppContainer/selectors';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/messages';
import JudiciaryProcessTabs from 'components/JudiciaryProcessTabs';
import {
  OFFER_QUITTANCE_INBOXE_TITLE,
  TRANSACTIONS_TAB_LABELS,
} from 'containers/TransactionManagerTimeLine/constants';
import {
  isBlockStatusClaim,
  isLockedClaim,
} from 'containers/CourtManagerLegalProceedings/utils';
import { renderSearchQuery } from 'containers/ExpertAssignedMissionShow/utils';
import { PAGES } from 'routes';
import VictimInformation from 'containers/TransactionManagerTimeLine/VictimInfo/index';
import routesHelper from 'containers/services/routes-helper';
import {
  makeSelectContactTransaction,
  makeSelectTransactions,
} from 'containers/TransactionManagerTimeLine/selector';
import {
  formatLabelsTabsTransactions,
  isAssignedArea,
  isBlockedTransaction,
  isDoneTransaction,
} from 'containers/TransactionManagerTimeLine/utils';
import ContactVictim from 'containers/TransactionManagerTimeLine/ContactVictim/index';
import ExpertReport from 'containers/TransactionManagerTimeLine/ExpertReport';
import { priorityClaimReason } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { resolveSagisClaim } from 'helpers/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { CLAIM_STATUS } from 'containers/data-structure';
import OfferRegulation from 'containers/TransactionManagerTimeLine/OfferRegulation';
import { GLOBAL_STATUS, OPENING_STATE } from 'services/constants';
import { ClaimRecapType } from 'typings/claimTypes';

export interface TransactionProps {
  intl: IntlShape;
  match: { params: { claimId: number } };
  claimNumber: string;
  claimRecap: ClaimRecapType;
  resetSearchClaimsStore: Function;
  resetStoreClaim: Function;
  resetSagisInfoClaim: Function;
  resetLegalProceeding: Function;
  policyInfo: any;
  requestSearchClaims: any;
  requestGetTransaction: Function;
  resetUploadedDocument: Function;
  uploadedDocument: any;
  uploadDocument: Function;
  requestAddTransaction: Function;
  requestUpdateTransaction: Function;
  requestAddContactTransaction: Function;
  requestGetContactTransaction: Function;
  resetStoreTransaction: Function;
  requestUpdateContactTransaction: Function;
  requestGetExpertUsers: Function;
  corporealExperts: any;
  requestAssignedExpert: Function;
  riskKey: string;
  requestGetMissionTransaction: Function;
  requestMakeDecisionMissionExpertise: Function;
  requestGetMissionVictimExpertise: Function;
  requestUpdateMissionTransaction: Function;
  requestGetFeeNotesTransaction: Function;
  onPressFeeNote: Function;
  isConsultation: boolean;
  expertJudiciaryExpertise: any;
  requestImportMissionExpertise: Function;
  requestGetJudiciaryExpertReport: Function;
  requestMakeDecisionVictimInfo: Function;
  sagisInfo: any;
  currentVerticalTabs?: any;
  storedTransactionId?: any;
  requestLockClaim: Function;
  setLockedClaim: Function;
  requestSetPriorityClaimProvision: Function;
  requestResetHistoryStore: Function;
  currentIndexTabManager: any;
  currentInstanceTabIndexManager: any;
  currentGlobalTabManagerIndex: any;
  currentJudiciaryProccessId: any;
  requestRejectTransaction?: any;
  setCurrentIndexTabTransaction?: any;
  requestGetDahirAmount: Function;
  dahirAmount?: any;
  requestCreateCorporealRegulation: Function;
  requestGetRegulations: Function;
  regulations: any;
  requestSagisClaimInfo: Function;
  requestGetRegulation: Function;
  requestValidateRegulation: Function;
  requestAddQuotationDocument?: Function;
  requestGetDocByDocumentumId: Function;
  isIntervenant?: boolean;
}

const Transaction: React.FC<TransactionProps> = (props): JSX.Element => {
  const transactions = useSelector(makeSelectTransactions());
  const listings = useSelector(makeSelectListings());
  const currentVerticalTabs = useSelector(
    makeSelectCurrentTabIndexTransaction(),
  );
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const constactTransaction = useSelector(makeSelectContactTransaction());
  useEffect(() => {
    if (props.storedTransactionId && transactions) {
      setCurrentTabIndex(
        indexOf(
          transactions,
          transactions.find((el: any) => el.id === props.storedTransactionId),
        ),
      );
    }
  }, [props.storedTransactionId, transactions]);
  const claimId = props.match.params.claimId;
  useEffect(() => {
    props.requestGetTransaction({
      claimId,
      isIntervenant: props.isIntervenant,
    });
  }, []);
  useEffect(() => {
    props.resetSearchClaimsStore();
    if (
      !isEmpty(props.claimRecap) &&
      !isNil(get(props.claimRecap, 'claim.claimNumber', null))
    ) {
      props.requestSagisClaimInfo(
        get(props.claimRecap, 'claim.claimNumber', null),
      );
      const searchQuery = renderSearchQuery(props.claimRecap);
      props.requestSearchClaims(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimRecap]);

  useDeepCompareEffect(() => {
    if (!isEmpty(get(props.policyInfo, 'sagis', {}))) {
      if (
        !isLockedClaim(props.policyInfo) &&
        featureService.hasFeature(FEATURES.AUTOMATIC_LOCK_CLAIM) &&
        !isNil(get(props.claimRecap, 'claim.claimNumber', null)) &&
        !isBlockStatusClaim(props.policyInfo) &&
        get(props, 'claimRecap.claim.status') === GLOBAL_STATUS.VALIDATED
      ) {
        props.requestLockClaim({
          claimNumber: get(props.claimRecap, 'claim.claimNumber'),
        });
        props.setLockedClaim(get(props.claimRecap, 'claim.claimNumber'));
      }
    }
  }, [props.claimRecap, props.policyInfo]);
  useEffect(() => {
    if (
      !isEmpty(get(props.policyInfo, 'sagis', [])) &&
      featureService.hasFeature(FEATURES.PRIORITY_CLAIM) &&
      (isNil(get(props.claimRecap, 'claim.priorityReasonId', null)) ||
        get(props.claimRecap, 'claim.priorityReasonId', null) != 4)
    ) {
      const priorityReason = priorityClaimReason(
        [],
        !isEmpty(get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []))
          ? get(resolveSagisClaim(props.policyInfo), 'offeredRisks', [])
              .map((el: any) => Number(el.riskProvisions))
              .reduce(
                (accumulator: any, currentValue: any) =>
                  accumulator + currentValue,
              )
          : 0,
        listings,
      );
      if (priorityReason) {
        props.requestSetPriorityClaimProvision({
          id: Number(claimId),
          claimInfo: { priorityReasonId: priorityReason },
          username: currentUserStorage.get().username,
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
        });
      }
    }
  }, [props.policyInfo]);
  useEffect(() => {
    if (
      !isEmpty(get(props.policyInfo, 'sagis', {})) &&
      !isEmpty(props.claimRecap)
    ) {
      if (isLockedClaim(props.policyInfo)) {
        toast.error(
          translate({ ...messages.errorLockedClaim }, props, {
            username: get(
              resolveSagisClaim(props.policyInfo),
              'lockedBy',
              null,
            ),
          }) as string,
        );
        props.resetStoreClaim();
        props.resetSearchClaimsStore();
        props.requestResetHistoryStore();
        routesHelper.pushTo(
          `${PAGES.CONSULTATION_LEGAL_PROCEEDING_PAGE}/${get(props, [
            'match',
            'params',
            'claimId',
          ])}`,
        )();
      } else if (
        isBlockStatusClaim(props.policyInfo) ||
        get(props.claimRecap, 'claim.status') != CLAIM_STATUS.VALIDATED ||
        get(props.claimRecap, 'claim.openingState') != OPENING_STATE.COMPLETED
      ) {
        props.resetStoreClaim();
        props.resetSearchClaimsStore();
        routesHelper.pushTo(
          `${PAGES.CONSULTATION_LEGAL_PROCEEDING_PAGE}/${get(props, [
            'match',
            'params',
            'claimId',
          ])}`,
        )();
      }
    }
  }, [
    props.policyInfo,
    props.currentIndexTabManager,
    props.currentInstanceTabIndexManager,
    props.currentGlobalTabManagerIndex,
    props.currentJudiciaryProccessId,
    props.claimRecap,
  ]);
  useEffect(() => {
    if (
      (!isEmpty(props.claimRecap) &&
        (get(props.claimRecap, 'claim.status') !== GLOBAL_STATUS.VALIDATED ||
          get(props.claimRecap, 'claim.openingState') !=
            OPENING_STATE.COMPLETED)) ||
      (!isEmpty(get(props.policyInfo, 'sagis', {})) &&
        isBlockStatusClaim(props.policyInfo))
    ) {
      props.resetSearchClaimsStore();
      props.resetStoreClaim();
      props.resetSagisInfoClaim();
      props.resetLegalProceeding();
      routesHelper.pushTo(
        `${PAGES.CONSULTATION_LEGAL_PROCEEDING_PAGE}/${get(props, [
          'match',
          'params',
          'claimId',
        ])}`,
      )();
    }
  }, [props.policyInfo, props.claimRecap]);
  const handleClickVerticalTabs = (tabIndex: any) => {
    if (props.setCurrentIndexTabTransaction) {
      props.setCurrentIndexTabTransaction(tabIndex);
      props.resetStoreTransaction();
    }
  };

  return (
    <ToggleTabs
      currentTabIndex={currentTabIndex}
      labels={formatLabelsTabsTransactions(transactions)}
    >
      {!isEmpty(transactions) &&
        transactions.map((el: any) => (
          <>
            <JudiciaryProcessTabs
              currentTabIndex={
                isNil(currentVerticalTabs) ? 0 : currentVerticalTabs
              }
              labels={TRANSACTIONS_TAB_LABELS}
              isTransactionTabs={true}
              handleClickVerticalTabsTransaction={handleClickVerticalTabs}
            >
              <VictimInformation
                victim={el.victim}
                listings={listings}
                transactionDocuments={get(el, 'documents', [])}
                resetUploadedDocument={props.resetUploadedDocument}
                uploadedDocument={props.uploadedDocument}
                uploadDocument={props.uploadDocument}
                requestAddTransaction={props.requestAddTransaction}
                requestUpdateTransaction={props.requestUpdateTransaction}
                transactionId={el.id}
                requestGetTransaction={props.requestGetTransaction}
                claimId={claimId}
                claimRecap={props.claimRecap}
                isConsultation={
                  props.isConsultation ||
                  isBlockedTransaction(el) ||
                  isDoneTransaction(el)
                }
                transactionInfo={el}
                policyInfo={props.policyInfo}
                claimNumber={props.claimNumber}
                riskKey={props.riskKey}
                requestMakeDecisionVictimInfo={
                  props.requestMakeDecisionVictimInfo
                }
                requestRejectTransaction={props.requestRejectTransaction}
                dahirAmounts={props.dahirAmount}
                requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
                requestGetDahirAmount={props.requestGetDahirAmount}
              />
              <ContactVictim
                requestAddContactTransaction={
                  props.requestAddContactTransaction
                }
                requestGetContactTransaction={
                  props.requestGetContactTransaction
                }
                contactTransaction={constactTransaction}
                transactionId={el.id}
                listings={listings}
                victim={el.victim}
                transactionDocuments={get(el, 'documents', [])}
                claimId={claimId}
                requestUpdateContactTransaction={
                  props.requestUpdateContactTransaction
                }
                resetStoreTransaction={props.resetStoreTransaction}
                corporealExperts={props.corporealExperts}
                requestGetExpertUsers={props.requestGetExpertUsers}
                requestAssignedExpert={props.requestAssignedExpert}
                claimNumber={props.claimNumber}
                riskKey={props.riskKey}
                requestGetMissionTransaction={
                  props.requestGetMissionTransaction
                }
                requestUpdateMissionTransaction={
                  props.requestUpdateMissionTransaction
                }
                isConsultation={
                  props.isConsultation ||
                  isBlockedTransaction(el) ||
                  isDoneTransaction(el)
                }
                expertJudiciaryExpertise={props.expertJudiciaryExpertise}
                requestImportMissionExpertise={
                  props.requestImportMissionExpertise
                }
                requestGetJudiciaryExpertReport={
                  props.requestGetJudiciaryExpertReport
                }
                sagisInfo={props.sagisInfo}
                transactionInfo={el}
                lossDate={get(props.claimRecap, 'claim.lossDate')}
                requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
                requestGetTransaction={props.requestGetTransaction}
              />
              <ExpertReport
                requestMakeDecisionMissionExpertise={
                  props.requestMakeDecisionMissionExpertise
                }
                claimId={claimId}
                claimNumber={props.claimNumber}
                riskKey={props.riskKey}
                requestGetMissionVictimExpertise={
                  props.requestGetMissionVictimExpertise
                }
                transactionId={el.id}
                setOpenFeeNoteDialog={() => {}}
                requestGetFeeNotesTransaction={
                  props.requestGetFeeNotesTransaction
                }
                onPressFeeNote={props.onPressFeeNote}
                isConsultation={
                  props.isConsultation ||
                  isBlockedTransaction(el) ||
                  isDoneTransaction(el)
                }
                transactionInfo={el}
                listings={listings}
                responsibilityShare={get(el, 'scaleCase.responsibilityShare')}
                victims={get(props.claimRecap, 'victims', [])}
                lossDate={get(props.claimRecap, 'claim.lossDate')}
                requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
              />
              <OfferRegulation
                victim={el.victim}
                listings={listings}
                transactionId={el.id}
                claimId={claimId}
                resetStoreTransaction={props.resetStoreTransaction}
                claimNumber={props.claimNumber}
                riskKey={props.riskKey}
                isConsultation={
                  props.isConsultation ||
                  isBlockedTransaction(el) ||
                  isDoneTransaction(el) ||
                  !isAssignedArea(el, listings, OFFER_QUITTANCE_INBOXE_TITLE)
                }
                sagisInfo={props.sagisInfo}
                transactionInfo={el}
                requestGetDahirAmount={props.requestGetDahirAmount}
                dahirAmount={props.dahirAmount}
                claimInfo={props.claimRecap}
                requestCreateCorporealRegulation={
                  props.requestCreateCorporealRegulation
                }
                policyInfo={props.policyInfo}
                requestGetRegulations={props.requestGetRegulations}
                regulations={props.regulations}
                requestSagisClaimInfo={props.requestSagisClaimInfo}
                requestGetRegulation={props.requestGetRegulation}
                requestValidateRegulation={props.requestValidateRegulation}
                resetUploadedDocument={props.resetUploadedDocument}
                uploadDocument={props.uploadDocument}
                uploadedDocument={props.uploadedDocument}
                requestAddQuotationDocument={props.requestAddQuotationDocument}
                requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
              />
            </JudiciaryProcessTabs>
          </>
        ))}
    </ToggleTabs>
  );
};
export default injectIntl(Transaction);
